import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

export const THEMES = [
  { id: "parchment", name: "Sacred Parchment", dark: false, preview: ["hsl(45 30% 96%)", "hsl(150 15% 20%)", "hsl(25 35% 55%)"] },
  { id: "ink", name: "Deep Ink", dark: true, preview: ["hsl(160 14% 8%)", "hsl(40 30% 82%)", "hsl(28 52% 62%)"] },
  { id: "olive", name: "Olive Grove", dark: false, preview: ["hsl(80 22% 94%)", "hsl(100 26% 22%)", "hsl(46 60% 40%)"] },
  { id: "dawn", name: "Dawn Rose", dark: false, preview: ["hsl(22 48% 96%)", "hsl(350 38% 30%)", "hsl(16 68% 52%)"] },
  { id: "galilee", name: "Galilee", dark: true, preview: ["hsl(210 38% 10%)", "hsl(195 45% 76%)", "hsl(40 62% 62%)"] },
  { id: "vine", name: "Royal Vine", dark: true, preview: ["hsl(288 26% 10%)", "hsl(45 48% 78%)", "hsl(330 48% 66%)"] },
];

export const BACKDROPS = [
  { id: "none", name: "None" },
  { id: "cross-sunrise", name: "Sunrise Cross", src: "/share-bg/cross-sunrise.jpg" },
  { id: "light-rays", name: "Heavenly Light", src: "/share-bg/light-rays.jpg" },
  { id: "still-waters", name: "Still Waters", src: "/share-bg/still-waters.jpg" },
  { id: "dove-sky", name: "Dove at Dawn", src: "/share-bg/dove-sky.jpg" },
  { id: "wheat-field", name: "Harvest Gold", src: "/share-bg/wheat-field.jpg" },
];

const THEME_IDS = new Set(THEMES.map((t) => t.id));
const BACKDROP_IDS = new Set(BACKDROPS.map((b) => b.id));

function normalizeThemeId(id) {
  if (id === "dark") return "ink";
  if (id === "light") return "parchment";
  return THEME_IDS.has(id) ? id : "parchment";
}

function applyThemeClasses(id) {
  const meta = THEMES.find((t) => t.id === id) || THEMES[0];
  const root = document.documentElement;
  THEMES.forEach((t) => root.classList.remove(`theme-${t.id}`));
  root.classList.add(`theme-${meta.id}`);
  root.classList.toggle("dark", meta.dark);
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => normalizeThemeId(localStorage.getItem("attune_theme")));
  const [backdropId, setBackdropId] = useState(() => {
    const saved = localStorage.getItem("attune_backdrop");
    return BACKDROP_IDS.has(saved) ? saved : "none";
  });

  useEffect(() => {
    applyThemeClasses(themeId);
    localStorage.setItem("attune_theme", themeId);
    const meta = THEMES.find((t) => t.id === themeId);
    localStorage.setItem(meta?.dark ? "attune_last_dark" : "attune_last_light", themeId);
  }, [themeId]);

  useEffect(() => {
    localStorage.setItem("attune_backdrop", backdropId);
  }, [backdropId]);

  const setTheme = useCallback((id) => setThemeId(normalizeThemeId(id)), []);
  const setBackdrop = useCallback((id) => setBackdropId(BACKDROP_IDS.has(id) ? id : "none"), []);

  const toggle = useCallback(() => {
    setThemeId((current) => {
      const isDark = THEMES.find((t) => t.id === current)?.dark;
      const fallback = isDark ? "parchment" : "ink";
      const remembered = localStorage.getItem(isDark ? "attune_last_light" : "attune_last_dark");
      return normalizeThemeId(remembered || fallback);
    });
  }, []);

  const value = useMemo(() => ({
    themeId,
    theme: THEMES.find((t) => t.id === themeId) || THEMES[0],
    backdropId,
    backdrop: BACKDROPS.find((b) => b.id === backdropId) || BACKDROPS[0],
    setTheme,
    setBackdrop,
    toggle,
  }), [themeId, backdropId, setTheme, setBackdrop, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
